import { useState, useCallback, useEffect, useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useDropzone } from 'react-dropzone'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
import {
  Upload,
  FileText,
  Search,
  User,
  Building,
  Eye,
  Download,
  Trash2,
  Plus,
  FlaskConical,
  X,
  Mail,
  BarChart3,
  Sparkles
} from 'lucide-react'
import toast from 'react-hot-toast'
import { motion } from 'framer-motion'

import DocumentPreviewDialog from '@/components/DocumentPreviewDialog'
import MedicalConsultationPanel from '@/components/MedicalConsultationPanel'
import UnifiedMedicalTimeline from '@/components/UnifiedMedicalTimeline'
import { useAuth } from '@/context/AuthContext'
import OutlookImporter from '@/components/OutlookImporter'
import { WorkingUploader } from '@/components/WorkingUploader'
import MedicalDataDashboard, {
  type MedicalDashboardPreviewTarget
} from '@/components/MedicalDataDashboard'
import { getAnalysesResultats } from '@/lib/bulk-document-import'
import { fetchHealthEntries } from '@/lib/health'
import { getLabResults } from '@/lib/lab-results'
import {
  buildMedicalDocumentPreviewSubtitle,
  formatMedicalDocumentDate,
  getMedicalDocumentClinicalDate,
  getMedicalDocumentDoctor,
  getMedicalDocumentDisplayTitle,
  getMedicalDocumentManualAnnotation,
  getMedicalDocumentManualAnnotationUpdatedAt,
  getMedicalDocumentPrimaryFacts,
  getMedicalDocumentSummary,
  inferMedicalDocumentSpecialty
} from '@/lib/medical-document-presentation'
import {
  resolveMedicalDocumentPreviewUrl,
  resolveMedicalDocumentUrl
} from '@/lib/medical-document-storage'
import {
  buildMedicalDocumentDisplayDuplicateKey,
  compareMedicalDocumentsForDisplay,
  filterDisplayableMedicalDocuments
} from '@/lib/medical-document-display'
import { backfillMedicalDocumentAnalysis, simpleUploadDocument } from '@/lib/working-simple-upload'
import {
  getMedicalDocuments,
  updateMedicalDocument,
  deleteMedicalDocument,
  getDocumentTypeLabel,
  getDocumentTypeColor,
  getDocumentIcon,
  formatFileSize,
  validateDocumentInput
} from '@/lib/medical-documents'
import { getActiveMedications, getPatientMedicationHistory } from '@/lib/medications'
import type { AnalyseResultat, MedicalDocument, DocumentType } from '@/types/health'

interface DocumentFormData {
  title: string
  document_type: DocumentType
  description: string
  document_date: string
  practitioner_name: string
  medical_facility: string
  specialty: string
  tags: string[]
  is_urgent: boolean
  is_confidential: boolean
}

const initialFormData: DocumentFormData = {
  title: '',
  document_type: 'other',
  description: '',
  document_date: new Date().toISOString().split('T')[0],
  practitioner_name: '',
  medical_facility: '',
  specialty: '',
  tags: [],
  is_urgent: false,
  is_confidential: false
}

const documentTypes: { value: DocumentType; label: string }[] = [
  { value: 'blood_test', label: 'Analyse de sang' },
  { value: 'radiology', label: 'Imagerie médicale' },
  { value: 'prescription', label: 'Ordonnance' },
  { value: 'consultation_report', label: 'Compte-rendu consultation' },
  { value: 'discharge_summary', label: 'Compte-rendu hospitalisation' },
  { value: 'pathology', label: 'Anatomopathologie' },
  { value: 'cardiology', label: 'Cardiologie' },
  { value: 'specialist_report', label: 'Rapport de spécialiste' },
  { value: 'vaccination_record', label: 'Carnet de vaccination' },
  { value: 'insurance_document', label: 'Document assurance' },
  { value: 'other', label: 'Autre document' }
]

const commonTags = [
  'Urgent', 'RCH', 'Endométriose', 'Douleur', 'Inflammation',
  'Suivi', 'Contrôle', 'Bilan', 'Traitement', 'Hospitalisation'
]

interface PreviewDocumentState {
  title: string
  subtitle?: string | null
  fileUrl?: string | null
  document?: MedicalDocument | null
}

interface SpecialtyBucket {
  specialty: string
  documents: MedicalDocument[]
  count: number
  doctors: string[]
  lastDate: string | null
}

function uniqueText(values: Array<string | null | undefined>): string[] {
  return [...new Set(values.map((value) => value?.trim()).filter(Boolean) as string[])]
}

function getDocumentSortValue(document: MedicalDocument): number {
  const candidates = [
    getMedicalDocumentClinicalDate(document).rawDate,
    document.document_date,
    document.created_at
  ]

  for (const candidate of candidates) {
    const parsed = Date.parse(candidate)

    if (Number.isFinite(parsed)) {
      return parsed
    }
  }

  return 0
}

function getDocumentSearchTerms(document: MedicalDocument): string[] {
  const clinicalDate = getMedicalDocumentClinicalDate(document)
  const candidateValues = [
    document.title,
    document.file_name,
    document.description,
    getMedicalDocumentManualAnnotation(document),
    document.practitioner_name,
    document.medical_facility,
    inferMedicalDocumentSpecialty(document),
    getMedicalDocumentDoctor(document),
    getMedicalDocumentSummary(document),
    getMedicalDocumentDisplayTitle(document),
    getDocumentTypeLabel(document.document_type),
    clinicalDate.rawDate,
    formatMedicalDocumentDate(clinicalDate.rawDate),
    ...document.tags
  ]

  return candidateValues
    .filter((value): value is string => Boolean(value && value.trim()))
    .map((value) => value.toLocaleLowerCase('fr-FR'))
}

function deduplicateMedicalDocumentsForDisplay(documents: MedicalDocument[]): MedicalDocument[] {
  const uniqueDocuments = new Map<string, MedicalDocument>()

  for (const document of filterDisplayableMedicalDocuments(documents)) {
    const duplicateKey = buildMedicalDocumentDisplayDuplicateKey(document)
    const existingDocument = uniqueDocuments.get(duplicateKey)

    if (!existingDocument || compareMedicalDocumentsForDisplay(document, existingDocument) < 0) {
      uniqueDocuments.set(duplicateKey, document)
    }
  }

  return [...uniqueDocuments.values()].sort((left, right) => {
    const priorityDelta = compareMedicalDocumentsForDisplay(left, right)

    if (priorityDelta !== 0) {
      return priorityDelta
    }

    const sortDelta = getDocumentSortValue(right) - getDocumentSortValue(left)

    if (sortDelta !== 0) {
      return sortDelta
    }

    return right.created_at.localeCompare(left.created_at)
  })
}

function matchesDocumentSearch(document: MedicalDocument, searchTerm: string): boolean {
  const normalizedSearchTerm = searchTerm.trim().toLocaleLowerCase('fr-FR')

  if (!normalizedSearchTerm) {
    return true
  }

  return getDocumentSearchTerms(document).some((value) => value.includes(normalizedSearchTerm))
}

function DocumentCard({
  document,
  specialty,
  summary,
  onPreview,
  onAnnotate,
  onDelete
}: {
  document: MedicalDocument
  specialty: string
  summary: string
  onPreview: (document: MedicalDocument) => void
  onAnnotate: (document: MedicalDocument) => void
  onDelete: (document: MedicalDocument) => void
}) {
  const typeColor = getDocumentTypeColor(document.document_type)
  const typeIcon = getDocumentIcon(document.document_type)
  const clinicalDate = getMedicalDocumentClinicalDate(document)
  const doctorName = getMedicalDocumentDoctor(document)
  const manualAnnotation = getMedicalDocumentManualAnnotation(document)
  const manualAnnotationUpdatedAt = getMedicalDocumentManualAnnotationUpdatedAt(document)
  const primaryFacts = getMedicalDocumentPrimaryFacts(document)
  const displayTitle = getMedicalDocumentDisplayTitle(document)
  const priorityFacts = primaryFacts.slice(1).join(' · ')
  const secondaryTitle =
    document.title && document.title !== displayTitle ? document.title : null
  const visibleSummary = summary === 'Document médical importé' ? null : summary

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="card hover:border-primary-800/50 transition-colors"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4 flex-1">
          <div className="w-12 h-12 rounded-xl bg-dark-surface-high border border-dark-border flex items-center justify-center text-xl">
            {typeIcon}
          </div>

          <div className="flex-1 min-w-0">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <span className={`badge border text-xs ${typeColor}`}>
                {getDocumentTypeLabel(document.document_type)}
              </span>
              <span className="inline-flex items-center rounded-full border border-cyan-500/20 bg-cyan-500/10 px-2.5 py-1 text-xs text-cyan-100">
                {specialty}
              </span>
              {document.is_urgent && (
                <span className="badge border border-red-400/20 bg-red-400/10 text-red-400 text-xs">
                  Urgent
                </span>
              )}
            </div>

            <h3 className="font-semibold text-white">{displayTitle}</h3>
            <p className="mt-1 text-sm text-slate-300">{priorityFacts || document.title}</p>
            {secondaryTitle && (
              <p className="mt-2 text-xs text-slate-500 line-clamp-1">{secondaryTitle}</p>
            )}
            {visibleSummary && (
              <p className="mt-3 text-sm text-slate-300 line-clamp-2">{visibleSummary}</p>
            )}
            <div
              className={`mt-3 rounded-xl border px-3 py-3 ${
                manualAnnotation
                  ? 'border-amber-500/20 bg-amber-500/5'
                  : 'border-dashed border-amber-500/20 bg-amber-500/[0.03]'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[11px] uppercase tracking-[0.16em] text-amber-200/80">Note patient</p>
                  {manualAnnotation ? (
                    <>
                      <p className="mt-1 text-sm text-amber-50/90 line-clamp-3">{manualAnnotation}</p>
                      {manualAnnotationUpdatedAt && (
                        <p className="mt-2 text-[11px] text-amber-100/60">
                          Modifiee le {format(new Date(manualAnnotationUpdatedAt), 'd MMM yyyy', { locale: fr })}
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="mt-1 text-sm text-amber-100/70">
                      Ajouter une information importante a retenir pour ce document.
                    </p>
                  )}
                </div>

                <button
                  type="button"
                  onClick={() => onAnnotate(document)}
                  className="shrink-0 rounded-lg border border-amber-500/20 bg-amber-500/10 px-3 py-1.5 text-xs font-medium text-amber-100 transition-colors hover:bg-amber-500/15"
                >
                  {manualAnnotation ? 'Modifier' : 'Ajouter'}
                </button>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-3 text-sm text-slate-400 md:grid-cols-2">
              {doctorName && (
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  <span>{doctorName}</span>
                </div>
              )}

              {document.medical_facility && (
                <div className="flex items-center gap-2">
                  <Building className="w-4 h-4" />
                  <span>{document.medical_facility}</span>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mt-6 pt-4 border-t border-dark-border">
        <div className="text-xs text-slate-500">
          {clinicalDate.label} {formatMedicalDocumentDate(clinicalDate.rawDate)}
        </div>
        <div className="hidden text-xs text-slate-500">
          Ajouté le {format(new Date(document.created_at), 'd MMM yyyy', { locale: fr })}
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onPreview(document)}
            className="rounded-lg border border-dark-border bg-dark-surface px-3 py-2 text-sm text-slate-300 transition-colors hover:text-white"
            title="Aperçu du PDF"
            aria-label={`Aperçu du document ${document.title}`}
          >
            <Eye className="w-4 h-4" />
            Aperçu
          </button>

          {document.file_url && (
            <a
              href={document.file_url}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-lg border border-dark-border bg-dark-surface px-3 py-2 text-sm text-blue-400 transition-colors hover:text-blue-300"
            >
              <Download className="w-4 h-4" />
              Télécharger
            </a>
          )}

          <button
            type="button"
            onClick={() => onDelete(document)}
            className="rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-2 text-sm text-red-300 transition-colors hover:text-red-200"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </motion.div>
  )
}

function DocumentUpload({ onUpload }: { onUpload: (files: File[], metadata: DocumentFormData) => void }) {
  const [files, setFiles] = useState<File[]>([])
  const [formData, setFormData] = useState<DocumentFormData>(initialFormData)
  const [tagInput, setTagInput] = useState('')
  const [extracting, setExtracting] = useState(false)

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setFiles(acceptedFiles)

    // Auto-remplir le titre avec le nom du premier fichier
    if (acceptedFiles.length > 0 && !formData.title) {
      const fileName = acceptedFiles[0].name.replace(/\.[^/.]+$/, "")
      setFormData(prev => ({ ...prev, title: fileName }))
    }

    // Auto-détecter le type de document selon le nom
    if (acceptedFiles.length > 0) {
      const fileName = acceptedFiles[0].name.toLowerCase()
      let detectedType: DocumentType = 'other'

      if (fileName.includes('sang') || fileName.includes('blood') || fileName.includes('hémato')) {
        detectedType = 'blood_test'
      } else if (fileName.includes('radio') || fileName.includes('scanner') || fileName.includes('irm')) {
        detectedType = 'radiology'
      } else if (fileName.includes('ordonnance') || fileName.includes('prescription')) {
        detectedType = 'prescription'
      } else if (fileName.includes('consultation') || fileName.includes('rapport')) {
        detectedType = 'consultation_report'
      }

      if (detectedType !== 'other') {
        setFormData(prev => ({ ...prev, document_type: detectedType }))
      }
    }
  }, [formData.title])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.bmp', '.tiff'],
      'text/*': ['.txt', '.doc', '.docx'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
    },
    maxSize: 50 * 1024 * 1024, // 50MB
    multiple: true
  })

  const handleSubmit = async () => {
    if (files.length === 0) {
      toast.error('Veuillez sélectionner au moins un fichier')
      return
    }

    const validation = validateDocumentInput({
      patient_id: '',
      file_name: files[0].name,
      ...formData,
      tags: formData.tags
    })

    if (!validation.isValid) {
      toast.error(validation.errors[0])
      return
    }

    setExtracting(true)
    onUpload(files, formData)
  }

  const addTag = (tag: string) => {
    if (tag && !formData.tags.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag]
      }))
    }
    setTagInput('')
  }

  const removeTag = (tag: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }))
  }

  return (
    <div className="space-y-6">
      {/* Zone de drop */}
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-2xl p-8 text-center transition-colors cursor-pointer ${
          isDragActive
            ? 'border-primary-400 bg-primary-900/10'
            : 'border-dark-border hover:border-primary-600'
        }`}
      >
        <input {...getInputProps()} />
        <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
        {isDragActive ? (
          <p className="text-primary-300">Déposez les fichiers ici...</p>
        ) : (
          <div>
            <p className="text-white mb-2">
              Glissez vos documents médicaux ici ou cliquez pour sélectionner
            </p>
            <p className="text-sm text-slate-400">
              PDF, images, documents Word acceptés (max 50MB par fichier)
            </p>
          </div>
        )}
      </div>

      {/* Fichiers sélectionnés */}
      {files.length > 0 && (
        <div className="space-y-3">
          <h3 className="font-medium text-white">Fichiers sélectionnés:</h3>
          {files.map((file, index) => (
            <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-dark-surface-high border border-dark-border">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-sm text-white">{file.name}</p>
                  <p className="text-xs text-slate-400">{formatFileSize(file.size)}</p>
                </div>
              </div>
              <button
                onClick={() => setFiles(files.filter((_, i) => i !== index))}
                className="text-red-400 hover:text-red-300"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Métadonnées */}
      {files.length > 0 && (
        <div className="card space-y-4">
          <h3 className="text-lg font-semibold text-white">Informations du document</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-300 mb-2">Titre *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                className="input"
                placeholder="Ex: Analyse de sang du 15 mars"
              />
            </div>

            <div>
              <label className="block text-sm text-slate-300 mb-2">Type de document *</label>
              <select
                value={formData.document_type}
                onChange={(e) => setFormData(prev => ({ ...prev, document_type: e.target.value as DocumentType }))}
                className="input"
              >
                {documentTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm text-slate-300 mb-2">Date du document *</label>
              <input
                type="date"
                value={formData.document_date}
                onChange={(e) => setFormData(prev => ({ ...prev, document_date: e.target.value }))}
                className="input"
              />
            </div>

            <div>
              <label className="block text-sm text-slate-300 mb-2">Praticien</label>
              <input
                type="text"
                value={formData.practitioner_name}
                onChange={(e) => setFormData(prev => ({ ...prev, practitioner_name: e.target.value }))}
                className="input"
                placeholder="Dr. Martin"
              />
            </div>

            <div>
              <label className="block text-sm text-slate-300 mb-2">Établissement</label>
              <input
                type="text"
                value={formData.medical_facility}
                onChange={(e) => setFormData(prev => ({ ...prev, medical_facility: e.target.value }))}
                className="input"
                placeholder="Hôpital Saint-Louis"
              />
            </div>

            <div>
              <label className="block text-sm text-slate-300 mb-2">Spécialité</label>
              <input
                type="text"
                value={formData.specialty}
                onChange={(e) => setFormData(prev => ({ ...prev, specialty: e.target.value }))}
                className="input"
                placeholder="Gastroentérologie"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-slate-300 mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="textarea"
              placeholder="Description ou résumé du document..."
              rows={3}
            />
          </div>

          <div>
            <label className="block text-sm text-slate-300 mb-2">Tags</label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                className="input flex-1"
                placeholder="Ajouter un tag..."
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    addTag(tagInput)
                  }
                }}
              />
              <button
                type="button"
                onClick={() => addTag(tagInput)}
                className="btn-secondary"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>

            <div className="flex flex-wrap gap-2 mb-2">
              {commonTags.map(tag => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => addTag(tag)}
                  className="text-xs px-2 py-1 rounded-full bg-dark-surface-high text-slate-300 hover:text-white transition-colors"
                >
                  + {tag}
                </button>
              ))}
            </div>

            {formData.tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.tags.map(tag => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-primary-900/20 text-primary-300 text-xs border border-primary-800/40"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      className="hover:text-primary-100"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.is_urgent}
                onChange={(e) => setFormData(prev => ({ ...prev, is_urgent: e.target.checked }))}
                className="rounded border-dark-border bg-dark-surface"
              />
              <span className="text-sm text-slate-300">Document urgent</span>
            </label>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.is_confidential}
                onChange={(e) => setFormData(prev => ({ ...prev, is_confidential: e.target.checked }))}
                className="rounded border-dark-border bg-dark-surface"
              />
              <span className="text-sm text-slate-300">Document confidentiel</span>
            </label>
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => {
                setFiles([])
                setFormData(initialFormData)
              }}
              className="btn-ghost"
            >
              Annuler
            </button>
            <button
              onClick={handleSubmit}
              disabled={extracting}
              className="btn-primary"
            >
              {extracting ? (
                <>
                  <div className="spinner w-4 h-4" />
                  Traitement en cours...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  Ajouter le document
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default function MedicalDocuments() {
  const { user, profile } = useAuth()
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<'dashboard' | 'list' | 'consultation' | 'upload'>('list')
  const [showBulkImporter, setShowBulkImporter] = useState(false)
  const [showManualUpload, setShowManualUpload] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterType, setFilterType] = useState<DocumentType | ''>('')
  const [specialtyFilter, setSpecialtyFilter] = useState('')
  const [annotatedOnly, setAnnotatedOnly] = useState(false)
  const [previewDocument, setPreviewDocument] = useState<PreviewDocumentState | null>(null)
  const [isReanalyzing, setIsReanalyzing] = useState(false)
  const [annotationDraft, setAnnotationDraft] = useState('')
  const previewedMedicalDocument = previewDocument?.document ?? null

  // Queries
  const { data: queriedDocuments = [], isLoading: isAllDocumentsLoading } = useQuery({
    queryKey: ['medical-documents-all', user?.id],
    queryFn: () => getMedicalDocuments(user!.id, undefined, null),
    enabled: Boolean(user)
  })

  const allDocuments = useMemo(
    () => deduplicateMedicalDocumentsForDisplay(queriedDocuments),
    [queriedDocuments]
  )

  const { data: consultationLabResults = [], isLoading: isConsultationLabResultsLoading } = useQuery({
    queryKey: ['consultation-lab-results', user?.id],
    queryFn: () => getLabResults(user!.id, 1000),
    enabled: Boolean(user && (activeTab === 'consultation' || activeTab === 'dashboard'))
  })

  const { data: consultationHealthEntries = [], isLoading: isConsultationHealthEntriesLoading } = useQuery({
    queryKey: ['consultation-health-entries', user?.id],
    queryFn: () => fetchHealthEntries(user!.id, 90),
    enabled: Boolean(user && (activeTab === 'consultation' || activeTab === 'dashboard'))
  })

  const { data: consultationActiveMedications = [], isLoading: isConsultationActiveMedicationsLoading } = useQuery({
    queryKey: ['consultation-active-medications', user?.id],
    queryFn: () => getActiveMedications(user!.id),
    enabled: Boolean(user && (activeTab === 'consultation' || activeTab === 'dashboard'))
  })

  const { data: consultationMedicationHistory = [], isLoading: isConsultationMedicationHistoryLoading } = useQuery({
    queryKey: ['consultation-medication-history', user?.id],
    queryFn: () => getPatientMedicationHistory(user!.id),
    enabled: Boolean(user && (activeTab === 'consultation' || activeTab === 'dashboard'))
  })

  const { data: previewAnalyses = [] } = useQuery({
    queryKey: ['document-analyses', user?.id, previewedMedicalDocument?.id],
    queryFn: async () => {
      const analyses = await getAnalysesResultats(user!.id)
      return analyses.filter((analysis) => analysis.document_id === previewedMedicalDocument?.id)
    },
    enabled: Boolean(user && previewedMedicalDocument && previewedMedicalDocument.document_type === 'blood_test'),
  })

  const invalidateMedicalDocumentQueries = useCallback(() => {
    if (!user?.id) {
      return
    }

    const queryKeys = [
      ['medical-documents', user.id],
      ['medical-documents-all', user.id],
      ['medical-documents-count', user.id],
      ['documents-with-extracts', user.id],
      ['prescriptions', user.id],
      ['analyses-resultats', user.id],
      ['consultation-lab-results', user.id],
      ['consultation-health-entries', user.id],
      ['consultation-active-medications', user.id],
      ['consultation-medication-history', user.id],
      ['diagnostics', user.id],
      ['timeline-medicale', user.id]
    ] as const

    queryKeys.forEach((queryKey) => {
      void queryClient.invalidateQueries({ queryKey: [...queryKey] })
    })
  }, [queryClient, user?.id])

  useEffect(() => {
    setAnnotationDraft(previewedMedicalDocument ? getMedicalDocumentManualAnnotation(previewedMedicalDocument) || '' : '')
  }, [previewedMedicalDocument])

  // Mutations
  const uploadMutation = useMutation({
    mutationFn: async ({ files, metadata }: { files: File[]; metadata: DocumentFormData }) => {
      const results: MedicalDocument[] = []

      for (const file of files) {
        try {
          // Upload du fichier dans le bucket prive
          const uploadResult = await simpleUploadDocument(file, user!.id)

          if (!uploadResult.success || !uploadResult.documentId) {
            throw new Error(uploadResult.error || `Echec de l import de ${file.name}`)
          }

          const document = await updateMedicalDocument(uploadResult.documentId, {
            title: metadata.title || file.name.replace(/\.[^/.]+$/, ''),
            document_type: metadata.document_type,
            description: metadata.description || null,
            document_date: metadata.document_date,
            practitioner_name: metadata.practitioner_name || null,
            medical_facility: metadata.medical_facility || null,
            specialty: metadata.specialty || null,
            tags: metadata.tags,
            is_urgent: metadata.is_urgent,
            is_confidential: metadata.is_confidential
          })

          results.push(document)
        } catch (error) {
          console.error('Erreur lors du traitement du fichier:', file.name, error)
          throw new Error(`Erreur lors du traitement de ${file.name}: ${error instanceof Error ? error.message : 'Erreur inconnue'}`)
        }
      }

      return results
    },
    onSuccess: (results) => {
      toast.success(`${results.length} document(s) ajouté(s) avec succès`)
      invalidateMedicalDocumentQueries()
      setShowManualUpload(false)
      setActiveTab('list')
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Erreur lors de l\'ajout')
    }
  })

  const deleteMutation = useMutation({
    mutationFn: (documentId: string) => deleteMedicalDocument(documentId),
    onSuccess: () => {
      toast.success('Document supprimé')
      invalidateMedicalDocumentQueries()
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Erreur lors de la suppression')
    }
  })

  const annotationMutation = useMutation({
    mutationFn: async ({ document, annotation }: { document: MedicalDocument; annotation: string }) => {
      const nextAnnotation = annotation.trim()

      return updateMedicalDocument(document.id, {
        extracted_data: {
          ...(document.extracted_data || {}),
          manual_annotation: nextAnnotation || null,
          manual_annotation_updated_at: nextAnnotation ? new Date().toISOString() : null,
          manual_annotation_source: nextAnnotation ? 'patient_manual' : null
        }
      })
    },
    onSuccess: (updatedDocument) => {
      toast.success('Annotation enregistree')
      invalidateMedicalDocumentQueries()
      setPreviewDocument((currentPreview) => {
        if (!currentPreview || currentPreview.document?.id !== updatedDocument.id) {
          return currentPreview
        }

        return {
          ...currentPreview,
          title: getMedicalDocumentDisplayTitle(updatedDocument),
          subtitle:
            getMedicalDocumentPrimaryFacts(updatedDocument).slice(1).join(' · ') ||
            buildMedicalDocumentPreviewSubtitle(updatedDocument),
          document: updatedDocument
        }
      })
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Impossible d enregistrer l annotation')
    }
  })

  const handleUpload = (files: File[], metadata: DocumentFormData) => {
    uploadMutation.mutate({ files, metadata })
  }

  const openDocumentPreview = async (document: MedicalDocument) => {
    const fileUrl = await resolveMedicalDocumentPreviewUrl(document)

    setPreviewDocument({
      title: getMedicalDocumentDisplayTitle(document),
      subtitle:
        getMedicalDocumentPrimaryFacts(document).slice(1).join(' · ') || buildMedicalDocumentPreviewSubtitle(document),
      fileUrl,
      document
    })
  }

  const openDashboardPreview = async (target: MedicalDashboardPreviewTarget) => {
    const matchingDocument =
      allDocuments.find((document) => document.id === target.id) ||
      allDocuments.find(
        (document) =>
          document.title === target.title && document.document_date.slice(0, 10) === target.documentDate.slice(0, 10)
      )

    if (matchingDocument) {
      void openDocumentPreview(matchingDocument)
      return
    }

    const fileUrl = await resolveMedicalDocumentUrl(target.fileUrl)

    setPreviewDocument({
      title: target.title,
      subtitle:
        target.subtitle ||
        format(new Date(target.documentDate), 'd MMM yyyy', {
          locale: fr
        }),
      fileUrl,
      document: null
    })
  }

  const analyzeExistingDocuments = () => {
    if (!user) {
      return
    }

    void (async () => {
      setIsReanalyzing(true)

      try {
        const result = await backfillMedicalDocumentAnalysis(user.id)

        invalidateMedicalDocumentQueries()

        if (result.failed.length > 0) {
          toast.error(`Analyse partielle: ${result.analyzed} analyses, ${result.failed.length} erreurs`)
          return
        }

        toast.success(`${result.analyzed} document(s) analyses, ${result.skipped} deja traites`)
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Erreur lors de l analyse des documents')
      } finally {
        setIsReanalyzing(false)
      }
    })()
  }

  const handleDelete = (document: MedicalDocument) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer "${document.title}" ?`)) {
      deleteMutation.mutate(document.id)
    }
  }

  const handleAnnotate = (document: MedicalDocument) => {
    const currentAnnotation = getMedicalDocumentManualAnnotation(document)
    const nextAnnotation = window.prompt(
      `Note patient pour "${getMedicalDocumentDisplayTitle(document)}"`,
      currentAnnotation || ''
    )

    if (nextAnnotation === null) {
      return
    }

    annotationMutation.mutate({
      document,
      annotation: nextAnnotation
    })
  }

  const filteredDocuments = useMemo(() => {
    return allDocuments.filter((document) => {
      if (filterType && document.document_type !== filterType) {
        return false
      }

      if (annotatedOnly && !getMedicalDocumentManualAnnotation(document)) {
        return false
      }

      return matchesDocumentSearch(document, searchTerm)
    })
  }, [allDocuments, annotatedOnly, filterType, searchTerm])

  const specialtyBuckets = useMemo<SpecialtyBucket[]>(() => {
    const groupedDocuments = filteredDocuments.reduce((groups, document) => {
      const specialty = inferMedicalDocumentSpecialty(document)
      const bucket = groups.get(specialty) || []
      bucket.push(document)
      groups.set(specialty, bucket)
      return groups
    }, new Map<string, MedicalDocument[]>())

    return [...groupedDocuments.entries()]
      .map(([specialty, specialtyDocuments]) => {
        const sortedDocuments = [...specialtyDocuments].sort(
          (left, right) => getDocumentSortValue(right) - getDocumentSortValue(left)
        )

        return {
          specialty,
          documents: sortedDocuments,
          count: sortedDocuments.length,
          doctors: uniqueText(sortedDocuments.map((document) => getMedicalDocumentDoctor(document))),
          lastDate: sortedDocuments[0]
            ? getMedicalDocumentClinicalDate(sortedDocuments[0]).rawDate
            : null
        }
      })
      .sort((left, right) => {
        const rightDate = right.lastDate ? Date.parse(right.lastDate) : 0
        const leftDate = left.lastDate ? Date.parse(left.lastDate) : 0

        if (rightDate !== leftDate) {
          return rightDate - leftDate
        }

        if (right.count !== left.count) {
          return right.count - left.count
        }

        return left.specialty.localeCompare(right.specialty, 'fr')
      })
  }, [filteredDocuments])

  const visibleSpecialtyBuckets = specialtyBuckets.filter((bucket) => {
    if (!specialtyFilter) {
      return true
    }

    return bucket.specialty === specialtyFilter
  })

  const specialtyCounts = specialtyBuckets.map((bucket) => ({
    specialty: bucket.specialty,
    count: bucket.count
  }))

  const visibleDocumentGroups = visibleSpecialtyBuckets.map((bucket) => [
    bucket.specialty,
    bucket.documents
  ] as const)

  const visibleDocuments = visibleSpecialtyBuckets.flatMap((bucket) => bucket.documents)
  const visibleCategoryCount = new Set(visibleDocuments.map((document) => document.document_type)).size
  const totalImportedDocuments = allDocuments.length
  const visibleDateRange = useMemo(() => {
    if (visibleDocuments.length === 0) {
      return null
    }

    const sortedByDate = [...visibleDocuments].sort(
      (left, right) => getDocumentSortValue(right) - getDocumentSortValue(left)
    )
    const newestDate = formatMedicalDocumentDate(getMedicalDocumentClinicalDate(sortedByDate[0]).rawDate)
    const oldestDate = formatMedicalDocumentDate(
      getMedicalDocumentClinicalDate(sortedByDate[sortedByDate.length - 1]).rawDate
    )

    return newestDate === oldestDate ? newestDate : `${oldestDate} au ${newestDate}`
  }, [visibleDocuments])
  const hasActiveFilters = Boolean(searchTerm || filterType || specialtyFilter || annotatedOnly)
  const consultationLoading =
    isAllDocumentsLoading ||
    isConsultationLabResultsLoading ||
    isConsultationHealthEntriesLoading ||
    isConsultationActiveMedicationsLoading ||
    isConsultationMedicationHistoryLoading

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="space-y-2">
          <h1 className="text-2xl font-bold text-white">Documents médicaux</h1>
          <div className="flex flex-wrap gap-2 text-xs text-slate-300">
            <span className="rounded-full border border-dark-border bg-dark-surface-high px-3 py-1.5">
              {totalImportedDocuments} importés
            </span>
            <span className="rounded-full border border-dark-border bg-dark-surface-high px-3 py-1.5">
              Tri auto par date, catégorie et spécialité
            </span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={analyzeExistingDocuments}
            className="btn-secondary"
            disabled={isReanalyzing || !user}
          >
            <Sparkles className={`w-4 h-4 ${isReanalyzing ? 'animate-pulse' : ''}`} />
            {isReanalyzing ? 'Analyse IA...' : 'Relancer l’analyse IA'}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('upload')}
            className="btn-primary"
          >
            <Upload className="w-4 h-4" />
            Importer
          </button>
        </div>
      </div>

      {/* Onglets */}
      <div className="flex space-x-1 bg-dark-surface-high rounded-lg p-1">
        <button
          onClick={() => setActiveTab('dashboard')}
          className={`px-4 py-2 rounded-md transition-colors text-sm ${
            activeTab === 'dashboard'
              ? 'bg-primary-600 text-white'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          <BarChart3 className="w-4 h-4 mr-2 inline" />
          Base médicale
        </button>
        <button
          onClick={() => setActiveTab('list')}
          className={`px-4 py-2 rounded-md transition-colors text-sm ${
            activeTab === 'list'
              ? 'bg-primary-600 text-white'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          <FileText className="w-4 h-4 mr-2 inline" />
          Documents
        </button>
        <button
          onClick={() => setActiveTab('consultation')}
          className={`px-4 py-2 rounded-md transition-colors text-sm ${
            activeTab === 'consultation'
              ? 'bg-primary-600 text-white'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          <Sparkles className="w-4 h-4 mr-2 inline" />
          Consultation
        </button>
        <button
          onClick={() => setActiveTab('upload')}
          className={`px-4 py-2 rounded-md transition-colors text-sm ${
            activeTab === 'upload'
              ? 'bg-primary-600 text-white'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          <Upload className="w-4 h-4 mr-2 inline" />
          Importer
        </button>
      </div>

      {/* Contenu des onglets */}
      {activeTab === 'dashboard' && (
        <div className="space-y-6">
          <UnifiedMedicalTimeline
            documents={allDocuments}
            labResults={consultationLabResults}
            healthEntries={consultationHealthEntries}
            medicationHistory={consultationMedicationHistory}
            onPreviewDocument={openDocumentPreview}
            title="Timeline medicale"
            description="Documents, biologie, journal et traitements dans une seule chronologie."
            limit={16}
          />

          <MedicalDataDashboard
            documents={allDocuments}
            onViewDocument={openDashboardPreview}
          />
        </div>
      )}

      {activeTab === 'consultation' && (
        <MedicalConsultationPanel
          documents={allDocuments}
          labResults={consultationLabResults}
          healthEntries={consultationHealthEntries}
          activeMedications={consultationActiveMedications}
          medicationHistory={consultationMedicationHistory}
          patientName={profile?.full_name || user?.user_metadata?.full_name || null}
          isLoading={consultationLoading}
          onPreviewDocument={openDocumentPreview}
        />
      )}

      {activeTab === 'list' && (
        <div className="space-y-6">
          <div className="card space-y-4">
            <div className="flex flex-wrap gap-2 text-xs text-slate-300">
              <span className="rounded-full border border-dark-border bg-dark-surface-high px-3 py-1.5">
                {totalImportedDocuments} importés
              </span>
              <span className="rounded-full border border-dark-border bg-dark-surface-high px-3 py-1.5">
                {visibleDocuments.length} affichés
              </span>
              <span className="rounded-full border border-dark-border bg-dark-surface-high px-3 py-1.5">
                {visibleSpecialtyBuckets.length} spécialités
              </span>
              <span className="rounded-full border border-dark-border bg-dark-surface-high px-3 py-1.5">
                {visibleCategoryCount} catégories
              </span>
              {visibleDateRange && (
                <span className="rounded-full border border-dark-border bg-dark-surface-high px-3 py-1.5">
                  {visibleDateRange}
                </span>
              )}
            </div>

            <div className="flex flex-col gap-3 lg:flex-row">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input pl-10"
                  placeholder="Rechercher"
                />
              </div>

              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value as DocumentType | '')}
                className="input lg:w-64"
              >
                <option value="">Tous les types</option>
                {documentTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>

              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchTerm('')
                    setFilterType('')
                    setSpecialtyFilter('')
                    setAnnotatedOnly(false)
                  }}
                  className="btn-ghost whitespace-nowrap"
                >
                  Réinitialiser
                </button>
              )}
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setAnnotatedOnly((current) => !current)}
                className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${
                  annotatedOnly
                    ? 'border-amber-500 bg-amber-500/15 text-amber-100'
                    : 'border-dark-border bg-dark-surface text-slate-300 hover:text-white'
                }`}
              >
                Notes seulement
              </button>
              <button
                type="button"
                onClick={() => setSpecialtyFilter('')}
                className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${
                  specialtyFilter === ''
                    ? 'border-cyan-500 bg-cyan-500/15 text-cyan-100'
                    : 'border-dark-border bg-dark-surface text-slate-300 hover:text-white'
                }`}
              >
                Toutes
              </button>

              {specialtyCounts.map(({ specialty, count }) => (
                <button
                  key={specialty}
                  type="button"
                  onClick={() => setSpecialtyFilter(specialty)}
                  className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${
                    specialtyFilter === specialty
                      ? 'border-cyan-500 bg-cyan-500/15 text-cyan-100'
                      : 'border-dark-border bg-dark-surface text-slate-300 hover:text-white'
                  }`}
                >
                  {specialty} · {count}
                </button>
              ))}
            </div>

            {false && specialtyBuckets.length > 0 && (
              <div className="rounded-2xl border border-dark-border bg-dark-surface-high p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h2 className="text-sm font-semibold text-white">Specialites medicales</h2>
                    <p className="mt-1 text-xs text-slate-400">
                      Cliquez sur une specialite pour afficher les documents relies.
                    </p>
                  </div>

                  {specialtyFilter && (
                    <button
                      type="button"
                      onClick={() => setSpecialtyFilter('')}
                      className="rounded-full border border-dark-border bg-dark-surface px-3 py-1.5 text-xs text-slate-300 transition-colors hover:text-white"
                    >
                      Revenir a toutes
                    </button>
                  )}
                </div>

                <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                  {specialtyBuckets.map((bucket) => (
                    <button
                      key={bucket.specialty}
                      type="button"
                      onClick={() => setSpecialtyFilter(bucket.specialty)}
                      className={`rounded-2xl border p-4 text-left transition-colors ${
                        specialtyFilter === bucket.specialty
                          ? 'border-cyan-500 bg-cyan-500/15'
                          : 'border-dark-border bg-dark-surface hover:border-primary-800/50'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-white">{bucket.specialty}</p>
                          <p className="mt-1 line-clamp-2 text-xs text-slate-400">
                            {bucket.doctors.slice(0, 2).join(', ') || 'Medecin ou service non identifie'}
                          </p>
                        </div>
                        <span className="rounded-full border border-dark-border px-2 py-1 text-xs text-slate-300">
                          {bucket.count}
                        </span>
                      </div>

                      <p className="mt-3 text-xs text-slate-500">
                        {bucket.lastDate
                          ? `Dernier document ${formatMedicalDocumentDate(bucket.lastDate)}`
                          : 'Date non disponible'}
                      </p>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Liste des documents */}
          {isAllDocumentsLoading ? (
            <div className="card text-center py-8">
              <div className="spinner mx-auto" />
              <p className="text-slate-400 mt-4">Chargement des documents...</p>
            </div>
          ) : visibleDocuments.length === 0 ? (
            <div className="card text-center py-12">
              <FileText className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">
                {hasActiveFilters ? 'Aucun document' : 'Aucun document importé'}
              </h3>
              <div className="mt-6 flex justify-center gap-3">
                {hasActiveFilters ? (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchTerm('')
                      setFilterType('')
                      setSpecialtyFilter('')
                    }}
                    className="btn-secondary"
                  >
                    Réinitialiser
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setActiveTab('upload')}
                    className="btn-primary"
                  >
                    <Plus className="w-4 h-4" />
                    Importer
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {visibleDocumentGroups.map(([specialty, specialtyDocuments]) => (
                <section key={specialty} className="space-y-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <FlaskConical className="h-4 w-4 text-cyan-300" />
                      <h2 className="text-sm font-semibold text-slate-100">
                        {specialty}
                      </h2>
                      <span className="text-xs text-slate-500">{specialtyDocuments.length}</span>
                    </div>
                    <span className="text-xs text-slate-500">
                      {specialtyDocuments[0]
                        ? formatMedicalDocumentDate(getMedicalDocumentClinicalDate(specialtyDocuments[0]).rawDate)
                        : ''}
                    </span>
                  </div>

                  <div className="space-y-4">
                    {specialtyDocuments.map((document) => (
                      <DocumentCard
                        key={document.id}
                        document={document}
                        specialty={specialty}
                        summary={getMedicalDocumentSummary(document)}
                        onPreview={openDocumentPreview}
                        onAnnotate={handleAnnotate}
                        onDelete={handleDelete}
                      />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'upload' && (
        <div className="space-y-5">
          <div className="card space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-white">Importer</h2>
                <p className="text-sm text-slate-400">{totalImportedDocuments} documents importés</p>
              </div>

              <button
                type="button"
                onClick={() => setShowBulkImporter(true)}
                className="btn-primary"
              >
                <Upload className="w-4 h-4" />
                Dossier maître
              </button>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => setShowBulkImporter(true)}
                className="btn-secondary"
              >
                <Upload className="w-4 h-4" />
                Importer un dossier
              </button>

              <button
                type="button"
                onClick={() => setShowManualUpload((previous) => !previous)}
                className="btn-ghost"
              >
                {showManualUpload ? 'Fermer le mode manuel' : 'Ajout manuel'}
              </button>
            </div>
          </div>

          {showManualUpload && (
            <div className="card">
              <DocumentUpload onUpload={handleUpload} />
            </div>
          )}

          <div className="card">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-500/15 text-blue-300">
                <Mail className="h-5 w-5" />
              </div>
              <h2 className="text-lg font-semibold text-white">Outlook</h2>
            </div>
            <OutlookImporter />
          </div>
        </div>
      )}

      {previewDocument && (
        <DocumentPreviewDialog
          title={previewDocument.title}
          subtitle={previewDocument.subtitle}
          fileUrl={previewDocument.fileUrl}
          onClose={() => setPreviewDocument(null)}
          footer={
            previewDocument.fileUrl ? (
              <div className="flex justify-end">
                <a
                  href={previewDocument.fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary"
                >
                  <Download className="w-4 h-4" />
                  Télécharger
                </a>
              </div>
            ) : undefined
          }
        >
          {previewedMedicalDocument && (
            <div className="space-y-6">
              {previewedMedicalDocument.title !== getMedicalDocumentDisplayTitle(previewedMedicalDocument) && (
                <div>
                  <label className="text-sm text-slate-400">Document source</label>
                  <p className="text-white">{previewedMedicalDocument.title}</p>
                </div>
              )}

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="text-sm text-slate-400">
                    {getMedicalDocumentClinicalDate(previewedMedicalDocument).label}
                  </label>
                  <p className="text-white">
                    {formatMedicalDocumentDate(getMedicalDocumentClinicalDate(previewedMedicalDocument).rawDate)}
                  </p>
                </div>

                <div>
                  <label className="text-sm text-slate-400">Spécialité</label>
                  <p className="text-white">{inferMedicalDocumentSpecialty(previewedMedicalDocument)}</p>
                </div>

                {previewedMedicalDocument.practitioner_name && (
                  <div>
                    <label className="text-sm text-slate-400">Praticien</label>
                    <p className="text-white">Dr. {previewedMedicalDocument.practitioner_name}</p>
                  </div>
                )}

                {previewedMedicalDocument.medical_facility && (
                  <div>
                    <label className="text-sm text-slate-400">Établissement</label>
                    <p className="text-white">{previewedMedicalDocument.medical_facility}</p>
                  </div>
                )}
              </div>

              <div>
                <label className="text-sm text-slate-400">Résumé court</label>
                <p className="mt-1 text-white">{getMedicalDocumentSummary(previewedMedicalDocument)}</p>
              </div>

              <div className="space-y-3 rounded-2xl border border-dark-border bg-dark-surface-high p-4">
                <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <label className="text-sm text-slate-300">Annotation importante</label>
                    <p className="text-xs text-slate-500">
                      Ajouter un contexte utile, une vigilance ou une note medicale.
                    </p>
                  </div>

                  {getMedicalDocumentManualAnnotationUpdatedAt(previewedMedicalDocument) && (
                    <p className="text-xs text-slate-500">
                      Mise a jour{' '}
                      {format(
                        new Date(getMedicalDocumentManualAnnotationUpdatedAt(previewedMedicalDocument)!),
                        'd MMM yyyy HH:mm',
                        { locale: fr }
                      )}
                    </p>
                  )}
                </div>

                <textarea
                  value={annotationDraft}
                  onChange={(event) => setAnnotationDraft(event.target.value)}
                  className="textarea min-h-[120px]"
                  placeholder="Ex: date de prelevement confirmee, resultat a surveiller, contexte clinique..."
                />

                <div className="flex flex-wrap justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setAnnotationDraft(getMedicalDocumentManualAnnotation(previewedMedicalDocument) || '')}
                    className="btn-secondary"
                    disabled={annotationMutation.isPending}
                  >
                    Revenir
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      annotationMutation.mutate({
                        document: previewedMedicalDocument,
                        annotation: annotationDraft
                      })
                    }
                    className="btn-primary"
                    disabled={
                      annotationMutation.isPending ||
                      annotationDraft.trim() === (getMedicalDocumentManualAnnotation(previewedMedicalDocument) || '').trim()
                    }
                  >
                    {annotationMutation.isPending ? 'Enregistrement...' : 'Enregistrer l annotation'}
                  </button>
                </div>
              </div>

              {previewedMedicalDocument.tags.length > 0 && (
                <div>
                  <label className="mb-2 block text-sm text-slate-400">Tags</label>
                  <div className="flex flex-wrap gap-2">
                    {previewedMedicalDocument.tags.map((tag) => (
                      <span
                        key={tag}
                        className="rounded-full border border-primary-800/40 bg-primary-900/20 px-2.5 py-1 text-xs text-primary-200"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {previewedMedicalDocument.document_type === 'blood_test' && previewAnalyses.length > 0 && (
                <div>
                  <label className="mb-3 block text-sm text-slate-400">Mesures extraites</label>
                  <div className="space-y-2">
                    {previewAnalyses.map((analysis: AnalyseResultat) => (
                      <div
                        key={analysis.id}
                        className="flex items-center justify-between rounded-lg border border-dark-border bg-dark-surface-high p-3"
                      >
                        <div>
                          <p className="font-medium text-white">{analysis.nom_analyse}</p>
                          {analysis.valeur_reference && (
                            <p className="text-xs text-slate-400">Réf: {analysis.valeur_reference}</p>
                          )}
                        </div>

                        <div className="text-right">
                          <p className="font-semibold text-white">
                            {[analysis.valeur, analysis.unite].filter(Boolean).join(' ')}
                          </p>
                          {analysis.statut && (
                            <p
                              className={`text-xs ${
                                analysis.statut === 'normal'
                                  ? 'text-emerald-300'
                                  : analysis.statut === 'limite'
                                    ? 'text-amber-300'
                                    : 'text-red-300'
                              }`}
                            >
                              {analysis.statut}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DocumentPreviewDialog>
      )}

      {/* Simple Uploader Modal */}
      {showBulkImporter && (
        <WorkingUploader
          onClose={() => setShowBulkImporter(false)}
          onComplete={() => {
            invalidateMedicalDocumentQueries()
          }}
        />
      )}
    </div>
  )
}
